import * as React from 'react';
import { Link } from '@mui/material';
import { useMixpanel } from 'react-mixpanel-browser';

import { VersionData, useDevice } from '@/api/devices';
import { VersionStateWithReleasedAt } from '@/api/ota/types';
import { Timestamp } from '@/components/data/Timestamp';
import { InlineAlertPrimaryButton, InlineAlertTitle } from '@/components/ui/InlineAlert';
import { useSpotterContext } from '@/context/SpotterContext';

export const USB_INSTRUCTIONS_HREF = 'https://www.sofarocean.com/posts/spotter-firmware-updates';

export const FIRMWARE_UPDATE_ALERT_TITLE = 'Firmware updates are available for your system';

export const FirmwareUpdateAlertTitle = () => <InlineAlertTitle>{FIRMWARE_UPDATE_ALERT_TITLE}</InlineAlertTitle>;

export const UsbInstallerAlertButton = ({ onClick }: { onClick?: () => void }) => (
    <InlineAlertPrimaryButton href={USB_INSTRUCTIONS_HREF} onClick={onClick}>
        Learn more
    </InlineAlertPrimaryButton>
);

export const UsbInstallerLink = ({ onClick }: { onClick?: () => void }) => (
    <Link href={USB_INSTRUCTIONS_HREF} onClick={onClick}>
        USB installer instructions
    </Link>
);

/**
 * Logs a metric for opening manual installation instructions, taking properties so we can distinguish different sources.
 */
export const useTrackUsbInstallerClick = () => {
    const mixpanel = useMixpanel();
    const { spotterId } = useSpotterContext();
    const { data: device } = useDevice();

    return (properties?: object) =>
        mixpanel?.track('Click firmware USB installer instructions', {
            spotterId,
            currentVersion: device?.versionInfo.currentVersion?.version,
            latestReleaseVersion: device?.versionInfo.latestReleaseVersion.version,
            ...properties,
        });
};

export const NewerVersionReleaseStatement = ({ version }: { version?: VersionStateWithReleasedAt | VersionData }) => {
    let spotterVersion: string | undefined;
    let releasedAt: string | undefined;
    if (version && 'spotterVersion' in version) {
        spotterVersion = `v${version.spotterVersion}`;
        releasedAt = version.spotterVersionReleasedAt;
    } else {
        spotterVersion = version?.version;
        releasedAt = version?.releasedAt;
    }

    if (!spotterVersion) {
        return <></>;
    }

    return (
        <>
            A newer firmware version <strong>{spotterVersion}</strong>{' '}
            {releasedAt ? (
                <>
                    was released on <Timestamp value={releasedAt} unit="date" />
                </>
            ) : (
                <>is available</>
            )}
            .
        </>
    );
};
